'use client';

import { cn } from '@utils/cn';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import type { CustomAllergen } from '../../allergy-matrix/allergens';
import { useAllergenLabel } from './use-allergen-label';

type AllergenBadgesProps = {
  allergens: readonly string[];
  customAllergens: readonly CustomAllergen[];
  className?: string;
  /** Shown when the dish has no allergens; nothing renders when omitted. */
  emptyLabel?: string;
};

/** A dish's allergens as small neutral chips, labelled for screen readers. */
const AllergenBadges: FC<AllergenBadgesProps> = ({
  allergens,
  customAllergens,
  className,
  emptyLabel
}) => {
  const t = useTranslations('allergy-matrix');
  const label = useAllergenLabel(customAllergens);
  if (!allergens.length) {
    return emptyLabel ? (
      <p className={cn('text-caption text-muted-foreground', className)}>
        {emptyLabel}
      </p>
    ) : null;
  }
  return (
    <ul
      aria-label={t('contains')}
      className={cn('flex flex-wrap gap-1.5', className)}
    >
      {allergens.map(key => (
        <li
          key={key}
          className="rounded-full bg-status-warning/15 px-2.5 py-0.5 text-meta font-medium text-foreground"
        >
          {label(key)}
        </li>
      ))}
    </ul>
  );
};

export default AllergenBadges;
